import { useQuery as useApolloQuery } from '@apollo/client';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import Loading from '@risksmart-app/components/src/loading';
import { handleError } from '@risksmart-app/components/src/utils/errorUtils';
import { mergeCustomI18n } from '@risksmart-app/i18n/src/i18n';
import { GetTaxonomyByLocaleAndOrgDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import i18next from 'i18next';
import type { FC, ReactNode } from 'react';
import { createContext } from 'react';

import { useIsFeatureFlagEnabled } from '../feature-flags/useIsFeatureFlagEnabled';
import { useTRPC } from '../utils/trpc';
import { ThirdPartyAuth0Context } from './ThirdPartyAuth0Context';

interface ITaxonomyContext {
  loading: boolean;
}

const TaxonomyContext = createContext<ITaxonomyContext | null>(null);

const TaxonomyProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const { user } = useRisksmartUser(ThirdPartyAuth0Context);
  const orgKey = user?.orgKey;

  // tRPC path (RSP-6570): the procedure scopes to the caller's org server-side,
  // so only the locale is sent — `orgKey` feeds only the legacy Apollo `where`.
  // No per-provider error handling on the tRPC branch — matching the web
  // TaxonomyProvider, which likewise reads only `{ data, loading }`. Query
  // errors are reported centrally by the QueryCache `onError` in utils/trpc.ts
  // (the portal mirror of web's RSP-6539 reporter), so a taxonomy failure
  // reaches Sentry with the same shape as every other tRPC query.
  const { data: trpcData, isLoading: trpcLoading } = useQuery(
    trpc.frontend.taxonomy.taxonomyByLocale.queryOptions(
      { locale: i18next.language },
      { enabled: trpcEnabled }
    )
  );

  // Legacy Apollo path. Skipped when tRPC is on so only one request fires and
  // the disabled branch can't warn (vitest promotes console.warn to fatal).
  const { data: apolloData, loading: apolloLoading } = useApolloQuery(
    GetTaxonomyByLocaleAndOrgDocument,
    {
      variables: {
        Locale: i18next.language,
        OrgKey: orgKey!,
      },
      fetchPolicy: 'no-cache',
      skip: trpcEnabled,
      onError: (error: unknown) => {
        handleError(
          `Error attempting to load translations for ${orgKey}: ${i18next.language}`
        );

        handleError(error);
      },
    }
  );

  const loading = trpcEnabled ? trpcLoading : apolloLoading;

  mergeCustomI18n(
    trpcEnabled
      ? (trpcData?.taxonomy ?? undefined)
      : apolloData?.taxonomy_org[0]?.taxonomy
  );

  return (
    <TaxonomyContext.Provider
      value={{
        loading,
      }}
    >
      {loading ? <Loading /> : children}
    </TaxonomyContext.Provider>
  );
};

export { TaxonomyProvider };
